import React from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/common/SiteHeader';
import { Check, Layers, Lock, FileEdit, MonitorPlay, Download } from 'lucide-react';

function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Hero Section - Centered with Soft Gradient */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-primary-100 to-primary-200">
        {/* Soft Gradient Shapes - Static for cross-browser compatibility */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Large soft blob top right */}
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-gradient-to-br from-primary-500/25 to-primary-400/15 rounded-full blur-3xl"></div>
          {/* Medium blob bottom left */}
          <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] bg-gradient-to-tr from-primary-600/20 to-primary-500/10 rounded-full blur-3xl"></div>
          {/* Center accent blob */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gradient-to-r from-primary-500/10 via-primary-400/15 to-primary-500/10 rounded-full blur-3xl"></div>

          {/* Small accent dots */}
          <div className="absolute top-20 left-[15%] w-3 h-3 bg-primary-500/40 rounded-full"></div>
          <div className="absolute top-32 right-[20%] w-2 h-2 bg-primary-400/50 rounded-full"></div>
          <div className="absolute bottom-32 left-[25%] w-4 h-4 bg-primary-600/30 rounded-full"></div>
          <div className="absolute bottom-20 right-[15%] w-2.5 h-2.5 bg-primary-500/35 rounded-full"></div>

          {/* Decorative rings */}
          <div className="absolute top-1/4 right-[10%] w-24 h-24 border border-primary-500/20 rounded-full"></div>
          <div className="absolute bottom-1/4 left-[8%] w-32 h-32 border border-primary-400/15 rounded-full"></div>
          <div className="absolute top-[60%] right-[25%] w-16 h-16 border border-primary-600/10 rounded-full"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-primary-700 mb-6 leading-tight">
            Video collections<br />made simple
          </h1>
          <p className="text-xl md:text-2xl text-primary-800/80 mb-12 max-w-3xl mx-auto leading-relaxed">
            All your video links in one place. From any platform.
          </p>

          {/* Feature Cards - Centered */}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12 max-w-6xl mx-auto">
            <FeatureCard
              icon={Layers}
              title="Multi-Platform"
              description="Collect from multiple platforms (YouTube, Vimeo and Dailymotion)"
            />
            <FeatureCard
              icon={FileEdit}
              title="Custom Context"
              description="Add your own titles and descriptions to any video"
            />
            <FeatureCard
              icon={Lock}
              title="Privacy Control"
              description="Share with anyone or invite-only collections"
            />
            <FeatureCard
              icon={MonitorPlay}
              title="Easy Sharing"
              description="Share with a link—no account needed to view"
            />
            <FeatureCard
              icon={Download}
              title="Bulk Import"
              description="Import entire YouTube channels in seconds. Search, select, done."
            />
          </div>

          <Link to="/register">
            <button className="group relative px-10 py-5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl shadow-lg hover:shadow-2xl text-xl font-bold transition-all duration-300 hover:scale-105 overflow-hidden">
              <span className="relative z-10">Create Your First Gallery</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
            </button>
          </Link>

          {/* Product Screenshot */}
          <div className="mt-16 max-w-5xl mx-auto">
            <div className="relative">
              <img
                src="/assets/galleries.png"
                alt="My Video Gallery dashboard showing gallery list with video thumbnails"
                className="w-full h-auto rounded-xl shadow-2xl border border-gray-200"
              />
              {/* Subtle glow effect behind screenshot */}
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 via-primary-400/10 to-primary-500/20 rounded-2xl blur-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase 1: Family Galleries - Image Left, Text Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[450px]">
              <img
                src="/assets/felipe-bustillo-4VDRCoNuvE0-unsplash.jpg"
                alt="Family enjoying movies"
                className="w-full h-full object-cover rounded-xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <h3 className="text-3xl md:text-4xl font-bold text-primary-700">
                Family Galleries
              </h3>
              <p className="text-lg text-primary-800 leading-relaxed">
                Create private video collections for family memories, special occasions, or themed content to share with a wider audience. Share unlisted videos without making them public, and control exactly who has access.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Keep family videos private and organized</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Share with selected users only</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Co-create collections for birthdays, weddings, holidays and other special events!</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase 2: Kids' Content - Image first on mobile, Text Left on desktop */}
      <section className="py-20 bg-primary-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[300px] md:h-[450px] lg:hidden">
              <img
                src="/assets/kids.jpg"
                alt="Children watching curated content"
                className="w-full h-full object-cover rounded-xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <h3 className="text-3xl md:text-4xl font-bold text-primary-700">
                Pre-selected Videos for Kids
              </h3>
              <p className="text-lg text-primary-800 leading-relaxed">
                Create collections of age-appropriate videos for your children. Predefine exactly what they can watch, combining educational content, entertainment and learning materials from various platforms in one safe space.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Handpick every video your kids can access - no more relying on platform algorithms</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Mix educational and entertainment content</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Build a favorites library - no more manual searching every time</span>
                </li>
              </ul>
            </div>
            <div className="relative h-[450px] hidden lg:block">
              <img
                src="/assets/kids.jpg"
                alt="Children watching curated content"
                className="w-full h-full object-cover rounded-xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase 3: Training Materials - Dark Overlay, Image Left, Text Right */}
      <section className="relative py-20 bg-primary-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[450px]">
              <img
                src="/assets/wonderlane-b9-odQi5oDo-unsplash.jpg"
                alt="Training session"
                className="w-full h-full object-cover rounded-xl shadow-2xl"
              />
            </div>
            <div className="space-y-6 text-white">
              <h3 className="text-3xl md:text-4xl font-bold">
                Training & Education
              </h3>
              <p className="text-lg text-gray-200 leading-relaxed">
                Build comprehensive learning paths by combining videos from multiple sources. Perfect for courses, tutorials and educational content.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-200">Structure content in any order</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-200">Mix content from different creators and sources</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-200">Add learning objectives and notes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase 4: Team Collections - Image first on mobile, Text Left on desktop */}
      <section className="py-20 bg-primary-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[300px] md:h-[450px] lg:hidden">
              <img
                src="/assets/priscilla-du-preez-XkKCui44iM0-unsplash.jpg"
                alt="Team collaborating"
                className="w-full h-full object-cover rounded-xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <h3 className="text-3xl md:text-4xl font-bold text-primary-700">
                Team Collections
              </h3>
              <p className="text-lg text-primary-800 leading-relaxed">
                Organize company resources, product demos, and reference materials for your team. Gather content from multiple platforms and add your own context to make it more valuable.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Centralize all video resources in one searchable location</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Share company knowledge without scattered bookmarks</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Control access with email invitations</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Build onboarding playlists for new team members</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-primary-800">Collaborate on collections with team editing permissions</span>
                </li>
              </ul>
            </div>
            <div className="relative h-[450px] hidden lg:block">
              <img
                src="/assets/priscilla-du-preez-XkKCui44iM0-unsplash.jpg"
                alt="Team collaborating"
                className="w-full h-full object-cover rounded-xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-700 mb-4">
              How We Compare
            </h2>
            <p className="text-xl text-gray-600">
              See why My Video Gallery is the better choice
            </p>
          </div>

          {/* Mobile: Compact table layout */}
          <div className="md:hidden">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-primary-300">
                  <th className="text-left py-3 pr-2 text-primary-800 font-semibold"></th>
                  <th className="py-3 px-1 text-center">
                    <img src="/assets/logo.png" alt="My Video Gallery" className="w-5 h-5 mx-auto" />
                  </th>
                  <th className="py-3 px-1 text-center text-gray-600 font-medium text-xs">Playlists</th>
                  <th className="py-3 px-1 text-center text-gray-600 font-medium text-xs">Manual</th>
                </tr>
              </thead>
              <tbody>
                <MobileComparisonRow feature="Multi-platform" us={true} competitor1={false} competitor2={true} />
                <MobileComparisonRow feature="Custom descriptions" us={true} competitor1={false} competitor2={true} />
                <MobileComparisonRow feature="Privacy control" us={true} competitor1={true} competitor2={false} />
                <MobileComparisonRow feature="No account needed" us={true} competitor1={false} competitor2={true} />
                <MobileComparisonRow feature="Organized & shareable" us={true} competitor1={true} competitor2={false} />
                <MobileComparisonRow feature="Bulk import" us={true} competitor1={false} competitor2={false} />
              </tbody>
            </table>
          </div>

          {/* Desktop: Table layout */}
          <div className="hidden md:block">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-primary-300">
                  <th className="text-left py-4 px-4 text-primary-800 font-semibold">Feature</th>
                  <th className="py-4 px-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 bg-opacity-10 rounded-lg border-2 border-primary-500">
                      <img src="/assets/logo.png" alt="" className="w-5 h-5" />
                      <span className="font-bold text-primary-700">My Video Gallery</span>
                    </div>
                  </th>
                  <th className="py-4 px-4 text-center text-gray-600 font-medium">Platform Playlists</th>
                  <th className="py-4 px-4 text-center text-gray-600 font-medium">Manual Lists</th>
                </tr>
              </thead>
              <tbody>
                <ComparisonRow
                  feature="Multi-platform support"
                  us={true}
                  competitor1={false}
                  competitor2={true}
                />
                <ComparisonRow
                  feature="Custom descriptions"
                  us={true}
                  competitor1={false}
                  competitor2={true}
                />
                <ComparisonRow
                  feature="Privacy control"
                  us={true}
                  competitor1={true}
                  competitor2={false}
                />
                <ComparisonRow
                  feature="No platform account needed"
                  us={true}
                  competitor1={false}
                  competitor2={true}
                />
                <ComparisonRow
                  feature="Organized & shareable"
                  us={true}
                  competitor1={true}
                  competitor2={false}
                />
                <ComparisonRow
                  feature="Bulk channel import"
                  us={true}
                  competitor1={false}
                  competitor2={false}
                />
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-primary-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-700 mb-12 text-center">
            FAQ
          </h2>

          <div className="space-y-8">
            <FAQItem
              question="1. Is My Video Gallery really free?"
              answer="Yes, completely free. There are no limits on the number of galleries or videos you can create, and no premium tiers. This service runs on donations only."
            />
            <FAQItem
              question="2. Do viewers need accounts to see my gallery?"
              answer="No. For public galleries, anyone with the link can view them. For private galleries, you invite people by email, and they don't need accounts on YouTube, Vimeo, or other platforms."
            />
            <FAQItem
              question="3. What video platforms are supported?"
              answer="Currently, we support YouTube, Vimeo, and Dailymotion. More platforms are coming soon!"
            />
            <FAQItem
              question="4. Can I make my gallery private?"
              answer="Yes. You can create public galleries (accessible to anyone with the link) or private galleries where you control who has access by inviting specific people via email."
            />
          </div>

          <div className="text-center mt-12">
            <Link to="/faq" onClick={() => window.scrollTo(0, 0)} className="text-primary-500 hover:text-primary-600 font-semibold text-lg underline">
              View all frequently asked questions →
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary-500 bg-opacity-10 rounded-full flex items-center justify-center">
              <img src="/assets/logo.png" alt="My Video Gallery" className="w-8 h-8" />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-primary-700 mb-6">
            Ready to organize your videos?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join others who are simplifying their video collections
          </p>
          <Link to="/register">
            <button className="px-10 py-4 bg-primary-500 hover:bg-primary-600 text-white rounded-lg shadow-lg hover:shadow-xl text-lg font-semibold transition-all duration-200">
              Create Your First Gallery
            </button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center text-gray-500 text-sm bg-primary-200 border-t border-primary-300">
        <div className="flex justify-center items-center gap-2 mb-4">
          <img src="/assets/logo.png" alt="My Video Gallery" className="w-8 h-8" />
          <span className="text-base font-semibold text-primary-700">My Video Gallery</span>
        </div>
        <div className="flex justify-center gap-8 mb-3">
          <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
          <Link to="/faq" className="hover:text-primary-500 transition-colors">FAQ</Link>
          <Link to="/contact" className="hover:text-primary-500 transition-colors">Contact</Link>
        </div>
        <span>&copy; {new Date().getFullYear()} My Video Gallery. All rights reserved.</span>
      </footer>
    </div>
  );
}

// Feature Card Component
function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div className="text-center p-6">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500 bg-opacity-10 rounded-full mb-4">
        <Icon className="w-8 h-8 text-primary-500" strokeWidth={2} />
      </div>
      <h3 className="text-xl font-bold text-primary-700 mb-2">{title}</h3>
      <p className="text-primary-800">{description}</p>
    </div>
  );
}

// Comparison Row Component
function ComparisonRow({ feature, us, competitor1, competitor2 }) {
  return (
    <tr className="border-b border-primary-300">
      <td className="py-4 px-4 text-primary-800">{feature}</td>
      <td className="py-4 px-4 text-center">
        {us ? (
          <Check className="w-6 h-6 text-primary-500 mx-auto" strokeWidth={2.5} />
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className="py-4 px-4 text-center">
        {competitor1 ? (
          <Check className="w-6 h-6 text-gray-400 mx-auto" strokeWidth={2} />
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className="py-4 px-4 text-center">
        {competitor2 ? (
          <Check className="w-6 h-6 text-gray-400 mx-auto" strokeWidth={2} />
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
    </tr>
  );
}

// FAQ Item Component
function FAQItem({ question, answer }) {
  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-primary-300">
      <h3 className="text-lg font-bold text-primary-700 mb-3">{question}</h3>
      <p className="text-primary-800 leading-relaxed">{answer}</p>
    </div>
  );
}

// Mobile Comparison Row Component
function MobileComparisonRow({ feature, us, competitor1, competitor2 }) {
  return (
    <tr className="border-b border-primary-300">
      <td className="py-3 pr-2 text-primary-800 text-sm">{feature}</td>
      <td className="py-3 px-1 text-center">
        {us ? (
          <Check className="w-5 h-5 text-primary-500 mx-auto" strokeWidth={2.5} />
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className="py-3 px-1 text-center">
        {competitor1 ? (
          <Check className="w-5 h-5 text-gray-400 mx-auto" strokeWidth={2} />
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className="py-3 px-1 text-center">
        {competitor2 ? (
          <Check className="w-5 h-5 text-gray-400 mx-auto" strokeWidth={2} />
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
    </tr>
  );
}

export default LandingPage;
